import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Input from "../../../components/ui/Input";
import {
  deleteProductService,
  getAllProductsService,
  getProductByIdService,
  updateProductService,
} from "redux/services/products_sevice";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const ProductTable = ({ products, sortConfig, onSort, onProductView }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);

  /* ================= Helpers ================= */
  const getStockBadge = (qty) => {
    if (qty === 0) return <Badge color="bg-error">{t("outOfStock")}</Badge>;
    if (qty <= 10) return <Badge color="bg-warning">{t("lowStock")}</Badge>;
    return <Badge color="bg-success">{t("inStock")}</Badge>;
  };

  const Badge = ({ color, children }) => (
    <span
      className={`
      inline-flex items-center justify-center
      h-5 min-h-[20px]
      px-2
      text-[10px] sm:text-xs
      leading-none
      rounded-full
      whitespace-nowrap
      w-fit
      ${color}
    `}
    >
      {children}
    </span>
  );

  const handleInputChange = (field, value) =>
    setEditValues((p) => ({ ...p, [field]: value }));

  /* ================= Actions ================= */
  const handleEdit = async (product) => {
    setLoadingEdit(true);
    setEditingId(product.id);
    await dispatch(getProductByIdService(product.id));
    setLoadingEdit(false);
    navigate("/edit-product");
  };

  const handleSave = async (id) => {
    try {
      await dispatch(
        updateProductService({
          id,
          formData: { ...editValues, _method: "PUT" },
        }),
      ).unwrap();

      toast.success(t("تم التحديث بنجاح"));
      dispatch(getAllProductsService({ page: 1 }));
      setEditingId(null);
      setEditValues({});
    } catch {
      toast.error(t("فشل في التحديث"));
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteProductService(deleteProductId)).unwrap();
      toast.success(t("تم الحذف بنجاح"));
      dispatch(getAllProductsService({ page: 1 }));
      setDeleteProductId(null);
    } catch {
      toast.error(t("فشل في الحذف"));
    }
  };

  /* ================= Fields (🔥 الأساس) ================= */
  const fields = [
    {
      key: "product",
      label: t("product"),
      renderDesktop: (p) => (
        <div className="flex gap-3">
          <Image src={p.images?.[0]?.file_path} className="w-12 h-12 rounded" />
          <div>
            <div className="font-medium">{p.translations?.ar?.name}</div>
            <div className="text-sm text-muted-foreground">
              {p.translations?.en?.desc}
            </div>
          </div>
        </div>
      ),
      renderMobile: (p) => (
        <div className="flex gap-3">
          <Image src={p.images?.[0]?.file_path} className="w-14 h-14 rounded" />
          <div className="font-medium">{p.translations?.ar?.name}</div>
        </div>
      ),
      primary: true,
    },
    {
      key: "category",
      label: t("category"),
      renderDesktop: (p) => <div>{p.category?.translations?.ar?.name}</div>,
      renderMobile: (p) => <div>{p.category?.translations?.ar?.name}</div>,
    },
    {
      key: "price",
      label: t("price"),
      renderDesktop: (p) => p.price.toFixed(2),
      renderMobile: (p) => `${p.price.toFixed(2)} `,
    },
    {
      key: "quantity",
      label: t("quantity"),
      renderDesktop: (p) => p.variants?.[0]?.quantity,
      renderMobile: (p) => p.variants?.[0]?.quantity,
    },
    {
      key: "status",
      label: t("status"),
      renderDesktop: (p) => getStockBadge(p.variants?.[0]?.quantity || 0),
      renderMobile: (p) => getStockBadge(p.variants?.[0]?.quantity || 0),
      header: true,
    },
  ];

  /* ================= Render ================= */
  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              {fields.map((f) => (
                <th
                  key={f.key}
                  onClick={() => onSort?.(f.key)}
                  className="p-4 cursor-pointer"
                >
                  {f.label}
                </th>
              ))}
              <th className="p-4">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b hover:bg-muted/40">
                {fields.map((f) => (
                  <td key={f.key} className="p-4">
                    {f.renderDesktop(p)}
                  </td>
                ))}
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    <Icon name="Edit2" onClick={() => handleEdit(p)} />
                    <Icon
                      name="Trash2"
                      className="text-error"
                      onClick={() => setDeleteProductId(p.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y">
        {products.map((p) => (
          <div key={p.id} className="p-4 space-y-3">
            <div className="flex justify-between">
              {fields.find((f) => f.primary)?.renderMobile(p)}
              {fields.find((f) => f.header)?.renderMobile(p)}
            </div>

            {fields
              .filter((f) => !f.primary && !f.header)
              .map((f) => (
                <div key={f.key} className="text-sm">
                  <strong>{f.label}:</strong> {f.renderMobile(p)}
                </div>
              ))}

            <div className="flex justify-end gap-3">
              <Icon name="Edit2" onClick={() => handleEdit(p)} />
              <Icon
                name="Trash2"
                className="text-error"
                onClick={() => setDeleteProductId(p.id)}
              />
            </div>
          </div>
        ))}
      </div>

      {deleteProductId && (
        <DeleteConfirmationModal
          isOpen
          onClose={() => setDeleteProductId(null)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

export default ProductTable;
